import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';

import { FireCommandService, FireForesightService } from 'src/api/services';
import { Site, FireCommandCameraConfig, MqttCameraConfig, AzureIotHubCameraConfig } from 'src/api/models';

type CameraConfigType = 'Mqtt' | 'AzureIotHub';

// The generated `FireCommandCameraConfig` model only declares the shared base fields.
// At runtime the backend serializes the full concrete (Mqtt/Azure) object, so we widen
// the type locally to read the type-specific properties once fetched. The type-specific
// fields are `Omit`-ted of the base fields before intersecting to avoid conflicting
// (required vs. optional) redeclarations of the same property.
type CameraConfigDetail = FireCommandCameraConfig
  & Partial<Omit<MqttCameraConfig, keyof FireCommandCameraConfig>>
  & Partial<Omit<AzureIotHubCameraConfig, keyof FireCommandCameraConfig>>;

@Component({
  selector: 'app-fire-foresight-camera-configs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ModalModule
  ],
  templateUrl: './fire-foresight-camera-configs.component.html',
  styleUrls: ['./fire-foresight-camera-configs.component.css']
})
export class FireForesightCameraConfigsComponent implements OnInit {

  // When true, this component is embedded inside another page (e.g. a tab on the
  // Fire Foresight Admin page) and should not render its own breadcrumb.
  @Input() embedded = false;

  public sites: Site[] = [];
  private siteNameById = new Map<number, string>();

  public configs: CameraConfigDetail[] = [];
  public loading = false;
  public page = 1;
  public pageSize = 20;
  public totalCount = 0;

  // null = All, 0 = MQTT (StandardMQTT), 1 = Azure IoT Hub
  public connectionModeFilter: 0 | 1 | null = null;

  // Add form state
  public newConfigType: CameraConfigType = 'Mqtt';
  public newSiteId: number | null = null;
  public newMqttConfig: Partial<MqttCameraConfig> = {};
  public newAzureConfig: Partial<AzureIotHubCameraConfig> = {};
  public newTouched: { [key: string]: boolean } = {};
  // The identifier (Client Id / Hub Device Id) defaults to the selected site's URI and is
  // read-only until this is checked, since that covers the vast majority of use cases.
  public newIdManualEdit = false;

  // Edit form state
  public editConfigType: CameraConfigType | null = null;
  public editSiteId: number | null = null;
  public editConfig: CameraConfigDetail = {} as CameraConfigDetail;
  public editTouched: { [key: string]: boolean } = {};

  constructor(
    private fireCommandService: FireCommandService,
    private fireForesightService: FireForesightService,
    private toasterService: ToastrService
  ) { }

  ngOnInit(): void {
    // Use the same Sites list as the Sites tab (FireForesightGetCameras returns a
    // stale/incomplete list) - a large pageSize is used since this endpoint is
    // paginated but the dropdown needs the full set.
    this.fireForesightService.FireForesightGetSites({ page: 0, pageSize: 1000 }).subscribe(result => {
      this.sites = result.items || [];
      this.siteNameById.clear();
      this.sites.forEach(site => this.siteNameById.set(site.siteId, site.name));
    });

    this.loadConfigs(1);
  }

  public getSiteName(siteId: number): string {
    return this.siteNameById.get(siteId) || `Site #${siteId}`;
  }

  // BadRequest responses return a plain-text message body rather than JSON, which
  // HttpClient surfaces as `error.error.text` instead of a parsed object.
  private getErrorMessage(err: unknown, fallback: string): string {
    const httpError = err as { error?: { text?: string; message?: string } };
    return httpError?.error?.text || httpError?.error?.message || fallback;
  }

  public loadConfigs(page: number = this.page): void {
    this.loading = true;
    // The backend's `page` param is 0-indexed, but the UI displays/tracks a 1-indexed
    // page number, so translate on the way in and back out.
    this.fireCommandService.FireCommandGetCameraConfigs({
      page: page - 1,
      pageSize: this.pageSize,
      connectionMode: this.connectionModeFilter
    }).subscribe({
      next: result => {
        this.configs = (result.items || []) as CameraConfigDetail[];
        this.page = result.page + 1;
        this.pageSize = result.pageSize;
        this.totalCount = result.totalCount;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toasterService.error('Failed to load camera configs');
      }
    });
  }

  public onFilterChange(): void {
    this.loadConfigs(1);
  }

  // Unsticks any camera configs stuck mid-provisioning on the backend.
  public unfreezeConfigs(): void {
    this.fireCommandService.FireCommandUnfreeze().subscribe({
      next: () => {
        this.toasterService.success('Camera configs unfrozen');
        this.loadConfigs(this.page);
      },
      error: err => this.toasterService.error(this.getErrorMessage(err, 'Failed to unfreeze camera configs'))
    });
  }

  public get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  public previousPage(): void {
    if (this.page > 1) {
      this.loadConfigs(this.page - 1);
    }
  }

  public nextPage(): void {
    if (this.page < this.totalPages) {
      this.loadConfigs(this.page + 1);
    }
  }

  public getConfigTypeLabel(row: CameraConfigDetail): string {
    // Pending (unprovisioned) rows have null broker/hub fields, so connectionMode -
    // present on every row regardless of provisioning state - must be checked first.
    if (row.connectionMode === 0) {
      return 'MQTT';
    }
    if (row.connectionMode === 1) {
      return 'Azure IoT Hub';
    }
    if (row.mqttBrokerUrl != null) {
      return 'MQTT';
    }
    if (row.azureHubHostName != null) {
      return 'Azure IoT Hub';
    }
    // Type can't be determined from the list payload alone when the "All" filter is
    // active and the raw JSON doesn't carry the type-specific fields either - falls
    // back to the requested filter, otherwise unknown until the row is opened for edit.
    if (this.connectionModeFilter === 0) {
      return 'MQTT';
    }
    if (this.connectionModeFilter === 1) {
      return 'Azure IoT Hub';
    }
    return 'Unknown';
  }

  public isExpired(row: CameraConfigDetail): boolean {
    return !!row.accessEnded;
  }

  // Backend provisioning fills in connection details asynchronously after create.
  public isPending(row: CameraConfigDetail): boolean {
    return !row.provisioned && !row.accessEnded;
  }

  // MQTT topics follow a fixed convention based on the client id: {clientId}/image,
  // {clientId}/commands, {clientId}/telemetry. Kept as visible (but read-only) fields
  // so the resulting topic names are still transparent to the user.
  private applyMqttTopics(config: { mqttTopicImage?: string; mqttTopicCommand?: string; mqttTopicTelemetry?: string }, clientId: string): void {
    const id = clientId || '';
    config.mqttTopicImage = `${id}/image`;
    config.mqttTopicCommand = `${id}/commands`;
    config.mqttTopicTelemetry = `${id}/telemetry`;
  }

  // ---- Add ----

  public openAddModal(): void {
    this.newConfigType = 'Mqtt';
    this.newSiteId = null;
    this.newMqttConfig = { useTls: true };
    this.newAzureConfig = {};
    this.newTouched = {};
    this.newIdManualEdit = false;
  }

  public markNewFieldTouched(field: string): void {
    this.newTouched[field] = true;
  }

  // MQTT topics are derived from the client id rather than entered manually.
  public onNewClientIdChange(clientId: string): void {
    this.applyMqttTopics(this.newMqttConfig, clientId);
  }

  public onNewSiteChange(siteId: number | null): void {
    this.newSiteId = siteId;
    this.applyDefaultIdentifier();
  }

  public onNewConfigTypeChange(configType: CameraConfigType): void {
    this.newConfigType = configType;
    this.applyDefaultIdentifier();
  }

  public onNewIdManualEditChange(manualEdit: boolean): void {
    this.newIdManualEdit = manualEdit;
    this.applyDefaultIdentifier();
  }

  // Defaults the connection identifier to the selected site's URI - the vast majority of
  // configs use this convention - unless the user has opted to edit it manually.
  private applyDefaultIdentifier(): void {
    if (this.newIdManualEdit) {
      return;
    }
    const siteUri = this.sites.find(site => site.siteId === this.newSiteId)?.siteUri || '';
    if (this.newConfigType === 'Mqtt') {
      this.newMqttConfig.mqttClientId = siteUri;
      this.onNewClientIdChange(siteUri);
    } else {
      this.newAzureConfig.azureHubDeviceId = siteUri;
    }
  }

  // Creation is now deferred-provisioning: only the site and connection identifier are
  // required up front, the backend provisioning service fills in the rest asynchronously.
  public isNewConfigValid(): boolean {
    if (!this.newSiteId) {
      return false;
    }
    if (this.newConfigType === 'Mqtt') {
      return !!this.newMqttConfig.mqttClientId;
    }
    return !!this.newAzureConfig.azureHubDeviceId;
  }

  public saveNewConfig(): void {
    this.newTouched.siteId = true;
    if (this.newConfigType === 'Mqtt') {
      this.newTouched.mqttClientId = true;
    } else {
      this.newTouched.azureHubDeviceId = true;
    }

    if (!this.isNewConfigValid()) {
      this.toasterService.error('Please fill in all required fields');
      return;
    }

    if (this.newConfigType === 'Mqtt') {
      this.fireCommandService.FireCommandCreateMqttCameraConfig({
        FireforesightCameraId: this.newSiteId,
        ConnectionMode: 0, // StandardMQTT - hardcoded, not user-facing
        CameraSerial: this.newMqttConfig.cameraSerial,
        MqttBrokerUrl: this.newMqttConfig.mqttBrokerUrl,
        MqttBrokerPort: this.newMqttConfig.mqttBrokerPort,
        MqttClientId: this.newMqttConfig.mqttClientId,
        MqttUsername: this.newMqttConfig.mqttUsername,
        MqttPassword: this.newMqttConfig.mqttPassword,
        MqttTopicImage: this.newMqttConfig.mqttTopicImage,
        MqttTopicCommand: this.newMqttConfig.mqttTopicCommand,
        MqttTopicTelemetry: this.newMqttConfig.mqttTopicTelemetry,
        UseTls: this.newMqttConfig.useTls,
        AzureBlobUrl: this.newMqttConfig.azureBlobUrl,
        AzureBlobSasToken: this.newMqttConfig.azureBlobSasToken
      }).subscribe({
        next: () => {
          this.toasterService.success('MQTT Camera Config added');
          this.loadConfigs(1);
        },
        error: err => this.toasterService.error(this.getErrorMessage(err, 'Failed to add MQTT camera config'))
      });
    } else {
      this.fireCommandService.FireCommandCreateAzureIotHubCameraConfig({
        FireforesightCameraId: this.newSiteId,
        ConnectionMode: 1, // IoTHub - hardcoded, not user-facing
        CameraSerial: this.newAzureConfig.cameraSerial,
        AzureHubHostName: this.newAzureConfig.azureHubHostName,
        AzureHubSasToken: this.newAzureConfig.azureHubSasToken,
        AzureHubDeviceId: this.newAzureConfig.azureHubDeviceId,
        AzureBlobUrl: this.newAzureConfig.azureBlobUrl,
        AzureBlobSasToken: this.newAzureConfig.azureBlobSasToken
      }).subscribe({
        next: () => {
          this.toasterService.success('Azure IoT Hub Camera Config added');
          this.loadConfigs(1);
        },
        error: err => this.toasterService.error(this.getErrorMessage(err, 'Failed to add Azure IoT Hub camera config'))
      });
    }
  }

  // ---- Edit ----

  public openEditModal(row: CameraConfigDetail, modalRef: { show: () => void }): void {
    this.fireCommandService.FireCommandGetCameraConfig(row.id).subscribe(detail => {
      const full = detail as CameraConfigDetail;
      this.editConfig = { ...full };
      this.editSiteId = full.fireforesightCameraId;
      this.editTouched = {};
      this.editConfigType = full.mqttBrokerUrl != null ? 'Mqtt' : 'AzureIotHub';
      modalRef.show();
    });
  }

  public markEditFieldTouched(field: string): void {
    this.editTouched[field] = true;
  }

  // MQTT topics are derived from the client id rather than entered manually.
  public onEditClientIdChange(clientId: string): void {
    this.applyMqttTopics(this.editConfig, clientId);
  }

  public isEditConfigValid(): boolean {
    if (!this.editSiteId) {
      return false;
    }
    if (this.editConfigType === 'Mqtt') {
      return !!(this.editConfig.mqttBrokerUrl && this.editConfig.mqttBrokerPort && this.editConfig.mqttClientId
        && this.editConfig.mqttUsername && this.editConfig.mqttPassword
        && this.editConfig.mqttTopicImage && this.editConfig.mqttTopicCommand && this.editConfig.mqttTopicTelemetry
        && this.editConfig.azureBlobUrl && this.editConfig.azureBlobSasToken);
    }
    return !!(this.editConfig.azureHubHostName && this.editConfig.azureHubSasToken
      && this.editConfig.azureHubDeviceId
      && this.editConfig.azureBlobUrl && this.editConfig.azureBlobSasToken);
  }

  public saveEditConfig(): void {
    this.editTouched.siteId = true;
    if (this.editConfigType === 'Mqtt') {
      ['mqttBrokerUrl', 'mqttBrokerPort', 'mqttClientId', 'mqttUsername', 'mqttPassword', 'mqttTopicImage', 'mqttTopicCommand',
        'mqttTopicTelemetry', 'azureBlobUrl', 'azureBlobSasToken'].forEach(field => this.editTouched[field] = true);
    } else {
      ['azureHubHostName', 'azureHubSasToken', 'azureHubDeviceId', 'azureBlobUrl', 'azureBlobSasToken']
        .forEach(field => this.editTouched[field] = true);
    }

    if (!this.isEditConfigValid()) {
      this.toasterService.error('Please fill in all required fields');
      return;
    }

    if (this.editConfigType === 'Mqtt') {
      this.fireCommandService.FireCommandUpdateMqttCameraConfig({
        Id: this.editConfig.id,
        id: this.editConfig.id,
        FireforesightCameraId: this.editSiteId,
        ConnectionMode: 0,
        CameraSerial: this.editConfig.cameraSerial,
        MqttBrokerUrl: this.editConfig.mqttBrokerUrl,
        MqttBrokerPort: this.editConfig.mqttBrokerPort,
        MqttClientId: this.editConfig.mqttClientId,
        MqttUsername: this.editConfig.mqttUsername,
        MqttPassword: this.editConfig.mqttPassword,
        MqttTopicImage: this.editConfig.mqttTopicImage,
        MqttTopicCommand: this.editConfig.mqttTopicCommand,
        MqttTopicTelemetry: this.editConfig.mqttTopicTelemetry,
        UseTls: this.editConfig.useTls,
        AzureBlobUrl: this.editConfig.azureBlobUrl,
        AzureBlobSasToken: this.editConfig.azureBlobSasToken
      }).subscribe({
        next: () => {
          this.toasterService.success('Camera Config updated');
          this.loadConfigs();
        },
        error: err => this.toasterService.error(this.getErrorMessage(err, 'Failed to update camera config'))
      });
    } else {
      this.fireCommandService.FireCommandUpdateAzureIotHubCameraConfig({
        Id: this.editConfig.id,
        id: this.editConfig.id,
        FireforesightCameraId: this.editSiteId,
        ConnectionMode: 1,
        CameraSerial: this.editConfig.cameraSerial,
        AzureHubHostName: this.editConfig.azureHubHostName,
        AzureHubSasToken: this.editConfig.azureHubSasToken,
        AzureHubDeviceId: this.editConfig.azureHubDeviceId,
        AzureBlobUrl: this.editConfig.azureBlobUrl,
        AzureBlobSasToken: this.editConfig.azureBlobSasToken
      }).subscribe({
        next: () => {
          this.toasterService.success('Camera Config updated');
          this.loadConfigs();
        },
        error: err => this.toasterService.error(this.getErrorMessage(err, 'Failed to update camera config'))
      });
    }
  }

  // ---- Finish / Expire (there is no delete - this is the only terminal action) ----

  public finishConfig(row: CameraConfigDetail): void {
    if (this.isExpired(row)) {
      return;
    }
    if (!confirm('Are you sure you want to do this? This action will disconnect the camera if it is already connected, ' +
      'and permanently close the license for this camera config. This cannot be undone. Continue?')) {
      return;
    }
    this.fireCommandService.FireCommandFinishCameraConfigAccess(row.id).subscribe({
      next: () => {
        this.toasterService.success('Camera Config license closed');
        this.loadConfigs();
      },
      error: err => this.toasterService.error(this.getErrorMessage(err, 'Failed to close camera config license'))
    });
  }
}
